import { ItemFormValues, ItemStatus, ItemType, LevelStatus, MonthResponse, PolicyData, RewardLevel, RewardRow, RowFilters, StatsSource } from './types';

export const num = (n?: number | null) =>
  String(Math.round(n || 0)).replace(/\B(?=(\d{3})+(?!\d))/g, '.');
export const vnd = (n?: number | null) => `${num(n)} đ`;

export const hoursText = (seconds?: number | null) => {
  const s = Math.max(0, Math.round(seconds || 0));
  const h = Math.floor(s / 3600);
  const m = Math.floor((s % 3600) / 60);
  return m ? `${h}h ${String(m).padStart(2, '0')}p` : `${h}h`;
};

export const monthLabel = (m?: string | null) => (m ? `${m.slice(5, 7)}/${m.slice(0, 4)}` : '—');
export const currentUtcMonth = () => new Date().toISOString().slice(0, 7);
/** Tháng vừa kết thúc (tháng nghiệp vụ 07:00 mùng 1 giờ VN = 00:00 UTC) — Bảng thưởng luôn trả cho tháng trước. */
export const previousUtcMonth = (now: Date = new Date()) => {
  const d = new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth() - 1, 1));
  return d.toISOString().slice(0, 7);
};

export const STATUS_LABEL: Record<LevelStatus, string> = {
  UP: 'Tăng bậc', KEEP: 'Duy trì', DOWN: 'Giảm bậc', NOT_QUALIFIED: 'Chưa đạt',
};
export const STATUS_COLOR: Record<LevelStatus, string> = {
  UP: 'magenta', KEEP: 'gold', DOWN: 'default', NOT_QUALIFIED: 'default',
};
export const SOURCE_LABEL: Record<StatsSource, string> = {
  MONTH_FILE: 'File tháng', DAILY: 'Cộng file ngày', PREV_COLUMNS: 'Cột "tháng trước"', NONE: 'Không có số',
};
export const ITEM_TYPES: ItemType[] = [
  'PKQT', 'BIRTHDAY', 'SPECIAL_EVENT', 'GUARANTEED_SALARY', 'OTHER_BONUS', 'NEW_CREATOR_OVERRIDE', 'LIVE_DEDUCT', 'VPNT',
];
export const ITEM_TYPE_LABEL: Record<ItemType, string> = {
  PKQT: 'NV E · PKQT',
  BIRTHDAY: 'NV F · Sinh nhật',
  SPECIAL_EVENT: 'Sự kiện đặc biệt',
  GUARANTEED_SALARY: 'Lương đảm bảo',
  OTHER_BONUS: 'Thưởng khác',
  NEW_CREATOR_OVERRIDE: 'Ghi đè NV B',
  LIVE_DEDUCT: 'Trừ ngày/giờ LIVE',
  VPNT: 'VPNT',
};
export const ITEM_STATUS_LABEL: Record<ItemStatus, string> = {
  PENDING: 'Chờ duyệt', CONFIRMED: 'Đã xác nhận', REJECTED: 'Từ chối', CANCELLED: 'Đã huỷ',
};
export const ITEM_STATUS_COLOR: Record<ItemStatus, string> = {
  PENDING: 'gold', CONFIRMED: 'green', REJECTED: 'red', CANCELLED: 'default',
};

export const DEFAULT_FILTERS: RowFilters = {
  q: '', group: '', status: '', onlyKc: true, onlyPending: false, onlyVpnt: false,
};

const fold = (s: string) => s.normalize('NFC').toLocaleLowerCase('vi').trim();

export function filterRows(rows: RewardRow[], f: RowFilters): RewardRow[] {
  const q = fold(f.q.replace(/^@/, ''));
  return rows.filter((r) => {
    if (f.onlyKc && r.kc <= 0) return false;
    if (f.group && r.groupName !== f.group) return false;
    if (f.status && r.status !== f.status) return false;
    if (f.onlyPending && !r.pendingItems) return false;
    if (f.onlyVpnt && !r.vpnt) return false;
    if (q) {
      const hay = [r.uid, r.tiktokUsername || '', r.userName || ''].map(fold);
      if (!hay.some((h) => h.includes(q))) return false;
    }
    return true;
  });
}

export function groupOptions(rows: RewardRow[]) {
  return Array.from(new Set(rows.map((r) => r.groupName).filter((g): g is string => !!g)))
    .sort((a, b) => a.localeCompare(b, 'vi'))
    .map((g) => ({ value: g, label: g }));
}

export function warningTexts(d: Pick<MonthResponse, 'counts' | 'warnings'> | any): string[] {
  const out: string[] = [];
  if (d.warnings?.missingDays?.length) {
    out.push(`Thiếu file ngày: ${d.warnings.missingDays.map((x: string) => x.slice(8)).join(', ')} — NV D các ngày này chưa tính.`);
  }
  if (d.counts?.notMonthFile) out.push(`${d.counts.notMonthFile} Creator đang tính từ file ngày (chưa có file trọn tháng).`);
  if (d.warnings?.prevColumns) out.push(`${d.warnings.prevColumns} Creator lấy số tháng trước từ cột "tháng trước" của file TikTok.`);
  if (d.warnings?.missingJoinDate) out.push(`${d.warnings.missingJoinDate} Creator thiếu ngày gia nhập — đang coi như Creator cũ.`);
  if (d.counts?.excluded) out.push(`${d.counts.excluded} UID bị loại theo Nhóm loại trừ / tài khoản Studio.`);
  return out;
}

/** AxiosUtils ném nguyên body lỗi của server ({ code, message }). */
export const rewardErrorMessage = (err: any): string =>
  err?.message || err?.error || err?.response?.data?.message || 'Không tải được dữ liệu. Thử lại sau.';

/** Nút mốc nhanh theo loại khoản, lấy từ policy đang hiệu lực (docs/thuong-online/02 §3.6). */
export function presetButtons(type: ItemType, presets?: PolicyData['presets']) {
  if (!presets) return [];
  if (type === 'PKQT') return presets.pkqt.map(([kc, amount]) => ({ label: `Mốc ${num(kc)} KC → ${vnd(amount)}`, amount, kc }));
  if (type === 'BIRTHDAY') return presets.birthday.map(([label, amount]) => ({ label: `${label} → ${vnd(amount)}`, amount, kc: null }));
  if (type === 'SPECIAL_EVENT') {
    const [kc, amount] = presets.specialEvent;
    return [{ label: `Đạt ${num(kc)} KC → ${vnd(amount)}`, amount, kc }];
  }
  return [];
}

/** Sửa một ô của bảng bậc, không đụng mảng/đối tượng cũ (dùng cho PolicyEditor). */
export function setLevelField(levels: RewardLevel[], index: number, key: keyof RewardLevel, value: number): RewardLevel[] {
  return levels.map((l, i) => (i === index ? { ...l, [key]: value } : l));
}

/** Thêm bậc mới dựa trên bậc cuối: bậc +1, KC/tiền +1.000.000. */
export function addLevel(levels: RewardLevel[]): RewardLevel[] {
  const last = levels[levels.length - 1];
  return [...levels, { ...last, level: last.level + 1, kc: last.kc + 1_000_000, down: last.down + 1_000_000, keep: last.keep + 1_000_000, up: last.up + 1_000_000 }];
}

/** Bớt bậc cuối, giữ tối thiểu 1 bậc. */
export function removeLastLevel(levels: RewardLevel[]): RewardLevel[] {
  return levels.length > 1 ? levels.slice(0, -1) : levels;
}

/** Đọc "500.000, 200.000, 100.000" → [500000, 200000, 100000]; sai định dạng → null; rỗng → []. */
export function parseNumberList(text: string): number[] | null {
  const parts = text.split(',').map((s) => s.trim()).filter(Boolean);
  const out = parts.map((s) => Number(s.replace(/\./g, '')));
  return out.every((n) => Number.isInteger(n) && n >= 0) ? out : null;
}

/** Form → body gửi lên POST/PUT /online-reward/items: giờ trừ đổi sang giây, ngày thành 'YYYY-MM-DD'. */
export function itemPayload(v: ItemFormValues, month: string): Record<string, unknown> {
  const deduct = v.type === 'LIVE_DEDUCT';
  const zero = deduct || v.type === 'VPNT';
  return {
    month,
    tiktokUid: String(v.tiktokUid || '').trim(),
    type: v.type,
    amount: zero ? 0 : v.amount ?? 0,
    kc: v.kc ?? null,
    deductDays: deduct ? v.deductDays ?? 0 : null,
    deductSeconds: deduct ? Math.round((v.deductHours ?? 0) * 3600) : null,
    eventDate: v.eventDate ? v.eventDate.format('YYYY-MM-DD') : null,
    note: v.note?.trim() || null,
    evidenceUrl: v.evidenceUrl?.trim() || null,
    confirmNow: v.confirmNow === true,
  };
}
